import { useFlag } from '@calendly/feature-check-react';
import { useFormContext } from '@calendly/form';
import { I18n } from '@calendly/i18n';
import { BodyText } from '@calendly/ui/components/typography';
import classNames from 'classnames';
import { useCallback, useEffect, useMemo, useRef, forwardRef } from 'react';
import { useGetOrganizationQuery, useGetUserQuery } from 'common';
import { buildNotetakerEtToggleSeenTrackingPayload, getNotetakerEtToggleDefaults, isNotetakerEtToggleEligible, NotetakerToggle, useNotetakerEtToggleExperimentVariant } from 'features/growth_experiments';
import { useGetMeetingIntelligenceUserSettingsQuery } from 'features/meeting_intelligence';
import { useTrack } from 'hooks/analytics_events';
import { useETEditorFormikPoolingType } from 'services/event_types';
import { NewConferenceError } from '../conference_error';
import { INTEGRATIONS } from '../constants';
import { useLocationManagement, useOrganizer, usePropsForInline } from '../hooks';
import { InlineChooser } from '../inlines';
import { SelectedLocation } from './selected_location';
import { DisplayOrganizer } from '../organizer/display_organizer';
import { OrganizerSelect } from '../organizer/organizer_select';
import { extractIntegrationsStatus, getIsRROrCRRWithoutCollectivePools, getMembershipIntegrationStatus, getUserPools } from '../utils';
const containerIsReadOnlyCss = "ch7d6r7";
const containerCss = "c1n6vl4g";
const Container = forwardRef((props, ref) => {
  const {
    isReadOnly,
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(containerCss, {
    [containerIsReadOnlyCss]: isReadOnly
  }, props.className)} />;
});
const wrapperCss = "wm83ua8";
const Wrapper = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(wrapperCss, props.className)} />;
});
const NotetakerToggleGate = ({
  eventTypeId,
  isDisabled,
  isLocked,
  isReadOnly,
  locationKind,
  onToggle,
  setFieldValue,
  setFieldTouched,
  values
}) => {
  const notetakerExperimentVariant = useNotetakerEtToggleExperimentVariant();
  const track = useTrack();
  const {
    data: meetingIntelligenceSettings,
    isLoading: isLoadingMiSettings,
    isFetching: isFetchingMiSettings
  } = useGetMeetingIntelligenceUserSettingsQuery();
  const notetakerDefaults = useMemo(() => getNotetakerEtToggleDefaults({
    variant: notetakerExperimentVariant,
    miSettings: meetingIntelligenceSettings,
    eventTypeId
  }), [eventTypeId, meetingIntelligenceSettings, notetakerExperimentVariant]);
  const shouldShowNotetakerToggle = notetakerDefaults.shouldShow;
  const hasUserToggledNotetakerRef = useRef(false);
  const lastAutoAppliedNotetakerDefaultRef = useRef(undefined);
  const hasSeenNotetakerToggle = Boolean(values.hasSeenNotetakerToggle);
  const notetakerFieldValue = values.notetakerEnabledForEt;
  const hasNotetakerFieldValue = typeof notetakerFieldValue === 'boolean';
  const notetakerEnabledForEt = Boolean(values.notetakerEnabledForEt);
  const isNotetakerToggleDisabled = isDisabled || isReadOnly || isLocked;
  useEffect(() => {
    hasUserToggledNotetakerRef.current = false;
    lastAutoAppliedNotetakerDefaultRef.current = undefined;
  }, [eventTypeId, locationKind]);
  useEffect(() => {
    const isMiSettingsLoading = isLoadingMiSettings || isFetchingMiSettings;
    const nextDefaultEnabled = Boolean(notetakerDefaults.defaultEnabled);
    if (!shouldShowNotetakerToggle || isMiSettingsLoading || hasUserToggledNotetakerRef.current || lastAutoAppliedNotetakerDefaultRef.current === nextDefaultEnabled) {
      return;
    }
    if (!hasNotetakerFieldValue || notetakerFieldValue !== nextDefaultEnabled) {
      setFieldValue('notetakerEnabledForEt', nextDefaultEnabled);
    }
    lastAutoAppliedNotetakerDefaultRef.current = nextDefaultEnabled;
  }, [hasNotetakerFieldValue, isFetchingMiSettings, isLoadingMiSettings, notetakerFieldValue, notetakerDefaults.defaultEnabled, setFieldValue, shouldShowNotetakerToggle]);
  const handleNotetakerToggle = useCallback(() => {
    hasUserToggledNotetakerRef.current = true;
    setFieldValue('notetakerEnabledForEt', !notetakerEnabledForEt);
    setFieldValue('hasUserToggledNotetaker', true);
    setFieldTouched('notetakerEnabledForEt');
    onToggle?.();
  }, [notetakerEnabledForEt, onToggle, setFieldTouched, setFieldValue]);
  useEffect(() => {
    const isMiSettingsLoading = isLoadingMiSettings || isFetchingMiSettings;
    if (!shouldShowNotetakerToggle || hasSeenNotetakerToggle || isMiSettingsLoading) return;
    track(buildNotetakerEtToggleSeenTrackingPayload({
      flow: eventTypeId ? 'edit' : 'create',
      variant: notetakerExperimentVariant,
      locationKind,
      defaultEnabled: Boolean(notetakerDefaults.defaultEnabled)
    }));
    setFieldValue('hasSeenNotetakerToggle', true, false);
  }, [eventTypeId, hasSeenNotetakerToggle, locationKind, notetakerDefaults.defaultEnabled, notetakerExperimentVariant, isFetchingMiSettings, isLoadingMiSettings, setFieldValue, shouldShowNotetakerToggle, track]);
  if (!shouldShowNotetakerToggle) return null;
  return <NotetakerToggle isEnabled={notetakerEnabledForEt} isDisabled={isNotetakerToggleDisabled} locationKind={locationKind} onToggle={handleNotetakerToggle} />;
};
const LocationNote = ({
  kind,
  showIntegrationError
}) => {
  if (INTEGRATIONS.includes(kind) && !showIntegrationError) {
    return <BodyText size="medium" color="muted">
        {I18n.t('publisher.event_type_editor.sections.event_details.location.conference_note')}
      </BodyText>;
  }
  if (kind === 'ask_invitee') {
    return <BodyText size="medium" color="muted">
        {I18n.t('publisher.event_types.location.ask_invitee.type_any_response')}
      </BodyText>;
  }
  return null;
};
export const SelectedLocationBase = ({
  location,
  membership,
  isLocked,
  eventType,
  showIntegrationErrors,
  possibleKinds,
  isDisabled,
  isReadOnly,
  conferenceOrganizer,
  ...rest
}) => {
  const {
    values,
    values: {
      locationConfigurations = []
    },
    setFieldValue,
    setFieldTouched
  } = useFormContext();
  const {
    isRoundRobin,
    isCollectiveRoundRobin
  } = useETEditorFormikPoolingType();
  const userPools = getUserPools(values);
  const isRROrCRRWithoutCollectivePools = getIsRROrCRRWithoutCollectivePools(isRoundRobin, isCollectiveRoundRobin, userPools);
  const {
    fieldNamePrefix,
    removeLocation,
    filteredLocations
  } = useLocationManagement(eventType, membership, location);
  const propsForInline = usePropsForInline(location.kind, eventType, userPools, filteredLocations);
  const {
    currentOrganizer
  } = useOrganizer(eventType, location.kind, locationConfigurations);
  const conferencingOrganizer = isRROrCRRWithoutCollectivePools ? conferenceOrganizer : currentOrganizer;
  const integrationStatuses = useMemo(() => extractIntegrationsStatus(eventType, conferencingOrganizer), [eventType, conferencingOrganizer]);
  const notetakerEnabledForEt = Boolean(values.notetakerEnabledForEt);
  const hasIntegrationError = INTEGRATIONS.includes(location.kind) && !integrationStatuses[location.kind];
  const organizerHasIntegrationError = getMembershipIntegrationStatus(conferencingOrganizer, location.kind);
  const showIntegrationError = showIntegrationErrors && (hasIntegrationError || organizerHasIntegrationError);
  const askInviteeNotification = isReadOnly && location.kind === 'ask_invitee';
  const {
    data: organization
  } = useGetOrganizationQuery();
  const {
    data: user
  } = useGetUserQuery();
  const hasMsTeamsFlag = useFlag('ms_teams_notetaker');
  const isNotetakerToggleEligible = useMemo(() => isNotetakerEtToggleEligible({
    eventType,
    locationKind: location.kind,
    integrationStatuses,
    organization,
    user,
    hasMsTeamsFlag
  }), [eventType, integrationStatuses, location.kind, organization, user, hasMsTeamsFlag]);
  const notetakerGateProps = useMemo(() => ({
    eventTypeId: eventType?.id,
    isDisabled,
    isLocked,
    isReadOnly,
    locationKind: location.kind,
    onToggle: null,
    setFieldValue,
    setFieldTouched,
    values
  }), [eventType?.id, isDisabled, isLocked, isReadOnly, location.kind, setFieldTouched, setFieldValue, values]);
  return <Container isReadOnly={isReadOnly} {...rest}>
      <Wrapper>
        <SelectedLocation isReadOnly={isReadOnly} kind={location.kind} onRemove={removeLocation} isDisabled={isLocked || isDisabled} isError={showIntegrationError} notetakerEnabledForEt={notetakerEnabledForEt} />
        {isReadOnly && INTEGRATIONS.includes(location.kind) && <DisplayOrganizer kind={location.kind} currentOrganizer={currentOrganizer} />}
        {!isReadOnly && <OrganizerSelect kind={location.kind} eventType={eventType} />}
        {(!isReadOnly || askInviteeNotification) && <LocationNote kind={location.kind} showIntegrationError={showIntegrationError} />}
        {isNotetakerToggleEligible && <NotetakerToggleGate {...notetakerGateProps} />}
        {showIntegrationError && <NewConferenceError kind={location.kind} organizer={conferencingOrganizer} hasBorder={isRROrCRRWithoutCollectivePools} isDisabled={isLocked || isDisabled} />}
      </Wrapper>
      <InlineChooser isReadOnly={isReadOnly} eventType={eventType} fieldNamePrefix={fieldNamePrefix} possibleKinds={possibleKinds} {...propsForInline} />
    </Container>;
};

require("./base.wyw-in-js.css!=!../../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./base.js");